import React from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  Link,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import {
  BetFilters,
  STATUS_OPTIONS,
  OUTCOME_OPTIONS,
  STAGE_OPTIONS,
  betFiltersActive,
  emptyBetFilters,
} from "./betFilters";
import "./MobileBets.scss";

interface BetsFilterBarProps {
  filters: BetFilters;
  setFilters: React.Dispatch<React.SetStateAction<BetFilters>>;
  teamOptions: string[];
}

const MENU_PROPS = { PaperProps: { style: { maxHeight: 320 } } };

type MultiKey = "status" | "outcome" | "stage";

const BetsFilterBar: React.FC<BetsFilterBarProps> = ({
  filters,
  setFilters,
  teamOptions,
}) => {
  const setKey = (key: MultiKey) => (values: string[]) =>
    setFilters((prev) => ({ ...prev, [key]: values }));

  return (
    <div className="betsFilter__bar">
      <MultiSelect
        label="Status"
        options={STATUS_OPTIONS}
        value={filters.status}
        onChange={setKey("status")}
      />
      <MultiSelect
        label="Outcome"
        options={OUTCOME_OPTIONS}
        value={filters.outcome}
        onChange={setKey("outcome")}
      />
      <MultiSelect
        label="Stage"
        options={STAGE_OPTIONS}
        value={filters.stage}
        onChange={setKey("stage")}
      />
      <Autocomplete
        multiple
        size="small"
        limitTags={2}
        options={teamOptions}
        value={filters.teams}
        onChange={(_, value) =>
          setFilters((prev) => ({ ...prev, teams: value }))
        }
        className="betsFilter__team"
        renderInput={(params) => (
          <TextField {...params} label="Team" placeholder="Team" />
        )}
      />
      {betFiltersActive(filters) && (
        <Button
          className="betsFilter__clear"
          startIcon={<FilterAltOffOutlinedIcon />}
          onClick={() => setFilters(emptyBetFilters)}
        >
          Clear
        </Button>
      )}
    </div>
  );
};

interface MultiSelectProps {
  label: string;
  options: string[];
  value: string[];
  onChange: (values: string[]) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const allSelected = options.length > 0 && value.length === options.length;
  const noneSelected = value.length === 0;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    onChange(typeof raw === "string" ? raw.split(",") : raw);
  };

  // Keep focus on the menu (don't let the link steal it) and stop the click
  // from bubbling into the Select's selection handling.
  const bulkAction = (next: string[]) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(next);
  };

  return (
    <FormControl size="small" className="betsFilter__control">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (selected as string[]).join(", ")}
        MenuProps={MENU_PROPS}
      >
        <ListSubheader className="betsFilter__menuActions">
          <Link
            component="button"
            type="button"
            underline="hover"
            disabled={allSelected}
            className="betsFilter__menuAction"
            onMouseDown={(e) => e.preventDefault()}
            onClick={bulkAction([...options])}
          >
            Select all
          </Link>
          <Link
            component="button"
            type="button"
            underline="hover"
            disabled={noneSelected}
            className="betsFilter__menuAction"
            onMouseDown={(e) => e.preventDefault()}
            onClick={bulkAction([])}
          >
            Clear all
          </Link>
        </ListSubheader>
        <Divider />
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={value.indexOf(option) > -1} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default BetsFilterBar;
